import { useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../useAxios";
import { useWorkspaceList, getUseWorkspace } from "./useWorkspaceList";
import { useWorkspaceMembersList, getUseWorkspaceMember } from "./useWorkspaceMembersList";
import { useWorkspaceMutations, useWorkspaceCrudMutations } from "./useWorkspaceMutations";
import { transformWorkspaceMember, transformWorkspace } from "./workspaceTransform";
import { getWorkspaceMemberFromCache } from "./useWorkspaceCache";
import { useCallback, useMemo } from "react";
import { USER_API } from "config";
import { normalizeId } from "utils";
import {
  WorkspaceFilters,
  InviteWorkspaceMemberParams,
  UpdateWorkspaceMemberParams,
  RemoveWorkspaceMemberParams,
  UseWorkspaceApiResult,
  UseWorkspaceMembersApiResult,
} from "./workspace.types";

/**
 * Main hook that brings together all workspace API functionality
 */
export const useWorkspaceApi = (filters: WorkspaceFilters = {}): UseWorkspaceApiResult => {
  const queryClient = useQueryClient();
  const axios = useAxios({ allResponse: false });

  // Set up workspace list functionality
  const {
    workspaces,
    rawWorkspaces,
    recommendedWorkspace,
    isLoading,
    isError,
    error,
    refetch,
    totalCount,
    workspaceCounts,
  } = useWorkspaceList(filters, axios);

  // Calculate workspaces count from existing data instead of separate API call
  const workspacesCount = useMemo(() => workspaces.length, [workspaces]);

  // Set up mutations
  const {
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    createWorkspaceAsync,
    updateWorkspaceAsync,
    deleteWorkspaceAsync,
    isCreating,
    isUpdating,
    isDeleting,
    createError,
    updateError,
    deleteError,
  } = useWorkspaceCrudMutations(axios, queryClient);

  // Create hook factory for individual workspace queries
  const useWorkspace = getUseWorkspace(axios, rawWorkspaces);

  // Get a workspace by ID (for one-time use)
  const getWorkspaceById = useCallback(
    async (workspaceId: string | number) => {
      const normalizedWorkspaceId = normalizeId(workspaceId);

      // Check cache first
      const cachedWorkspace = getWorkspaceFromCache(queryClient, normalizedWorkspaceId);
      if (cachedWorkspace) return cachedWorkspace;

      try {
        // Fetch from API if not in cache
        const data = (await axios.get(USER_API.getWorkspace(normalizedWorkspaceId))).data;
        return transformWorkspace(data.data || data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [axios, queryClient]
  );

  // Memoized function to get workspace from cache
  const getWorkspaceFromCache = useCallback((queryClient: any, workspaceId: string) => {
    const queryData = queryClient.getQueryData(["workspaces", "detail", workspaceId]);
    return queryData || null;
  }, []);

  // Return all functionality from the combined hooks with memoization
  return useMemo(
    () => ({
      // Query results
      workspaces,
      rawWorkspaces,
      recommendedWorkspace,
      isLoading,
      isError,
      error,
      totalCount,

      // Counts
      workspaceCounts,
      workspacesCount,

      // Operations
      createWorkspace: (params: { name: string; description?: string }) =>
        createWorkspace.mutate(params),
      createWorkspaceAsync,
      updateWorkspace: (params: { workspaceId: string; name?: string; description?: string }) =>
        updateWorkspace.mutate(params),
      updateWorkspaceAsync,
      deleteWorkspace: (workspaceId: string) => deleteWorkspace.mutate(workspaceId),
      deleteWorkspaceAsync,

      // Query hooks and utilities
      useWorkspace,
      getWorkspaceById,
      refetch,

      // Operation states
      isCreating,
      isUpdating,
      isDeleting,

      // Errors
      createError,
      updateError,
      deleteError,
    }),
    [
      workspaces,
      rawWorkspaces,
      recommendedWorkspace,
      isLoading,
      isError,
      error,
      totalCount,
      workspaceCounts,
      workspacesCount,
      createWorkspace,
      createWorkspaceAsync,
      updateWorkspace,
      updateWorkspaceAsync,
      deleteWorkspace,
      deleteWorkspaceAsync,
      refetch,
      // Removed useWorkspace and getWorkspaceById from dependency array to prevent infinite re-renders
      isCreating,
      isUpdating,
      isDeleting,
      createError,
      updateError,
      deleteError,
    ]
  );
};

/**
 * Main hook that brings together all workspace members API functionality
 */
export const useWorkspaceMembersApi = (
  workspaceId: string | number,
  filters: WorkspaceFilters = {}
): UseWorkspaceMembersApiResult => {
  const queryClient = useQueryClient();
  const axios = useAxios({ allResponse: false });
  const normalizedWorkspaceId = normalizeId(workspaceId);

  // Set up workspace members list functionality
  const {
    workspaceMembers,
    rawWorkspaceMembers,
    isLoading,
    isError,
    error,
    refetch,
    totalCount,
    workspaceMemberCounts,
  } = useWorkspaceMembersList(normalizedWorkspaceId, filters, axios);

  // Calculate workspace members count from existing data instead of separate API call
  const workspaceMembersCount = useMemo(() => workspaceMembers.length, [workspaceMembers]);

  // Set up mutations
  const {
    inviteMember,
    updateMember,
    removeMember,
    inviteMemberAsync,
    updateMemberAsync,
    removeMemberAsync,
    isInviting,
    isUpdating,
    isRemoving,
    inviteError,
    updateError,
    removeError,
  } = useWorkspaceMutations(normalizedWorkspaceId, axios, queryClient);

  // Create hook factory for individual workspace member queries
  const useWorkspaceMember = getUseWorkspaceMember(axios, rawWorkspaceMembers);

  // Get a workspace member by ID (for one-time use)
  const getWorkspaceMemberById = useCallback(
    async (memberId: string | number) => {
      const normalizedMemberId = normalizeId(memberId);

      // Check cache first
      const cachedMember = getWorkspaceMemberFromCache(
        queryClient,
        normalizedWorkspaceId,
        normalizedMemberId
      );
      if (cachedMember) return cachedMember;

      try {
        // Fetch from API if not in cache
        const data = (
          await axios.get(USER_API.getWorkspaceMember(normalizedWorkspaceId, normalizedMemberId))
        ).data;
        return transformWorkspaceMember(data.data || data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [axios, queryClient, normalizedWorkspaceId]
  );

  // Memoized function to get workspace member from cache
  const getWorkspaceMemberFromCacheCallback = useCallback(
    (memberId: string | number) =>
      getWorkspaceMemberFromCache(queryClient, normalizedWorkspaceId, normalizeId(memberId)),
    [queryClient, normalizedWorkspaceId]
  );

  // Return all functionality from the combined hooks with memoization
  return useMemo(
    () => ({
      // Query results
      workspaceMembers,
      rawWorkspaceMembers,
      isLoading,
      isError,
      error,
      totalCount,

      // Counts
      workspaceMemberCounts,
      workspaceMembersCount,

      // Operations
      inviteMember: (params: InviteWorkspaceMemberParams) => inviteMember.mutate(params),
      inviteMemberAsync,
      updateMember: (params: UpdateWorkspaceMemberParams) => updateMember.mutate(params),
      updateMemberAsync,
      removeMember: (params: RemoveWorkspaceMemberParams) => removeMember.mutate(params),
      removeMemberAsync,

      // Query hooks and utilities
      useWorkspaceMember,
      getWorkspaceMemberById,
      getWorkspaceMemberFromCache: getWorkspaceMemberFromCacheCallback,
      refetch,

      // Operation states
      isInviting,
      isUpdating,
      isRemoving,

      // Errors
      inviteError,
      updateError,
      removeError,
    }),
    [
      workspaceMembers,
      rawWorkspaceMembers,
      isLoading,
      isError,
      error,
      totalCount,
      workspaceMemberCounts,
      workspaceMembersCount,
      inviteMember,
      inviteMemberAsync,
      updateMember,
      updateMemberAsync,
      removeMember,
      removeMemberAsync,
      // Removed useWorkspaceMember, getWorkspaceMemberById, and getWorkspaceMemberFromCacheCallback from dependency array to prevent infinite re-renders
      refetch,
      isInviting,
      isUpdating,
      isRemoving,
      inviteError,
      updateError,
      removeError,
    ]
  );
};
